import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PhotoIcon,
  XMarkIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import SingleSelect from '../../components/SingleSelect';
import warehousePhotosService from '../../services/warehousePhotos.service';
import type { ProSummary, WarehousePhoto, WarehouseOption } from '../../types/warehousePhotos';

// =============================================================================
// Warehouse Photos (Order Management)
// =============================================================================
// Modern rebuild of the legacy whsephotos.pl. Two sections:
//   1. Upload photos to a PRO (lands on its Docs tab; warehouse-CSR is emailed)
//      OR a WAREHOUSE (a holding bucket).
//   2. Assign a warehouse's un-assigned photos to a pro (moves them onto it).
// The warehouse dropdown is the same list as the LC dashboard's "All Warehouses".

type Banner = { kind: 'success' | 'error'; text: string } | null;

const IMAGE_EXTS = /\.(jpe?g|png|gif|bmp|webp|tif?f|heic|heif)$/i;

// ---------------------------------------------------------------------------
// Pro lookup — input + Check, shows the six summary fields on success.
// ---------------------------------------------------------------------------
const ProLookup: React.FC<{
  label?: string;
  pro: ProSummary | null;
  onResolved: (pro: ProSummary | null) => void;
}> = ({ label = 'Pronumber', pro, onResolved }) => {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastLookedUp = useRef<string>(''); // avoid re-querying the same value on every blur

  const runLookup = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed) { setError(null); onResolved(null); lastLookedUp.current = ''; return; }
    if (trimmed === lastLookedUp.current) return; // already resolved this value
    setLoading(true);
    setError(null);
    try {
      onResolved(await warehousePhotosService.lookupPro(parseInt(trimmed, 10)));
      // Only remember SUCCESSFUL lookups — recording the value before the
      // request made a transient failure sticky (re-blurring the same
      // pronumber short-circuited above and never retried).
      lastLookedUp.current = trimmed;
    } catch (err: any) {
      // A real 404 means the pro doesn't exist; anything else is a failed
      // request and must not masquerade as "not found" (2026-08-25 sweep).
      setError(
        err?.response?.status === 404
          ? 'Pronumber not found'
          : 'Lookup failed — check your connection and try again.'
      );
      onResolved(null);
    } finally {
      setLoading(false);
    }
  }, [value, onResolved]);

  return (
    <div>
      <label className="block text-sm font-medium text-label mb-1">{label}</label>
      <div className="relative max-w-xs">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            setValue(digits);
            if (pro) onResolved(null);
          }}
          onBlur={runLookup}
          onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
          placeholder="Enter pronumber"
          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-panel px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
        />
        {loading && (
          <MagnifyingGlassIcon className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-pulse text-gray-400" />
        )}
      </div>
      {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {pro && (
        <div className="mt-3 overflow-x-auto rounded-md border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600 dark:text-gray-300">
                {['Pro No.', 'Move Type', 'Equipment #', 'BOL/BKG/Ref', 'Bill To', 'Customer'].map((h) => (
                  <th key={h} className="whitespace-nowrap px-3 py-1.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="text-body">
                <td className="whitespace-nowrap px-3 py-1.5">{pro.pronumber}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{pro.movetype}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{pro.container}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{pro.bol}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{pro.billtoname}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{pro.customername}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Staged file uploader — picks files, previews thumbnails, removes; the parent
// submits the batch. Matches the legacy "select → preview → Save" flow.
// ---------------------------------------------------------------------------
interface Staged {
  file: File;
  url: string; // object URL for preview (images only)
  isImage: boolean;
}

const StagedUploader: React.FC<{
  staged: Staged[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
}> = ({ staged, onAdd, onRemove, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const pick = (fl: FileList | null) => {
    if (fl && fl.length) onAdd(Array.from(fl));
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); if (!disabled) pick(e.dataTransfer.files); }}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          disabled ? 'cursor-not-allowed opacity-50 border-gray-300 dark:border-gray-600'
          : dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50/50 dark:hover:bg-slate-700/30'
        }`}
      >
        <ArrowUpTrayIcon className="h-7 w-7 text-gray-400" />
        <span className="text-sm text-gray-600 dark:text-gray-300">Drop photos here or click to select</span>
        <span className="text-xs text-gray-400">Images & video, up to 10MB each</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => { pick(e.target.files); e.target.value = ''; }}
        />
      </div>

      {staged.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {staged.map((s, i) => (
            <div key={i} className="relative h-24 w-24 overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700">
              {s.isImage ? (
                <img src={s.url} alt={s.file.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center p-1 text-center">
                  <PhotoIcon className="h-6 w-6 text-gray-400" />
                  <span className="mt-1 line-clamp-2 text-[0.625rem] text-gray-500">{s.file.name}</span>
                </div>
              )}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                className="absolute right-0.5 top-0.5 rounded-full bg-scrim/60 p-0.5 text-white hover:bg-scrim/80"
                title="Remove"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Warehouse photo card (section 2) — lazy-loads its thumbnail via the
// authenticated preview endpoint; selectable + removable.
// ---------------------------------------------------------------------------
const WarehousePhotoCard: React.FC<{
  photo: WarehousePhoto;
  selected: boolean;
  onToggle: () => void;
  onRemove: () => void;
}> = ({ photo, selected, onToggle, onRemove }) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    warehousePhotosService
      .previewBlob(photo.recno)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [photo.recno]);

  return (
    <div
      onClick={onToggle}
      className={`relative h-28 w-28 cursor-pointer overflow-hidden rounded-md border-2 bg-gray-100 dark:bg-gray-700 ${
        selected ? 'border-blue-500 ring-2 ring-blue-400' : 'border-gray-300 dark:border-gray-600'
      }`}
      title={photo.filename}
    >
      {url ? (
        <img src={url} alt={photo.filename} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <PhotoIcon className="h-7 w-7 text-gray-400 animate-pulse" />
        </div>
      )}
      {selected && (
        <div className="absolute left-1 top-1 rounded-full bg-blue-600 p-0.5 text-white">
          <CheckCircleIcon className="h-4 w-4" />
        </div>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute right-1 top-1 rounded-full bg-scrim/60 p-0.5 text-white hover:bg-red-600"
        title="Remove from warehouse"
      >
        <TrashIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const WarehousePhotos: React.FC = () => {
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);

  useEffect(() => {
    warehousePhotosService.getWarehouses().then(setWarehouses).catch(() => setWarehouses([]));
  }, []);
  const whseOptions = useMemo(
    () => warehouses.map((w) => ({ value: w.value, label: w.label })),
    [warehouses],
  );

  // --- Section 1: upload ---------------------------------------------------
  const [target, setTarget] = useState<'pro' | 'warehouse'>('pro');
  const [uploadPro, setUploadPro] = useState<ProSummary | null>(null);
  const [uploadWhse, setUploadWhse] = useState('');
  const [staged, setStaged] = useState<Staged[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadBanner, setUploadBanner] = useState<Banner>(null);

  const addStaged = useCallback((files: File[]) => {
    setStaged((prev) => [
      ...prev,
      ...files.map((file) => ({
        file,
        isImage: IMAGE_EXTS.test(file.name),
        url: IMAGE_EXTS.test(file.name) ? URL.createObjectURL(file) : '',
      })),
    ]);
  }, []);
  const removeStaged = useCallback((index: number) => {
    setStaged((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return next;
    });
  }, []);
  const clearStaged = useCallback(() => {
    setStaged((prev) => { prev.forEach((s) => s.url && URL.revokeObjectURL(s.url)); return []; });
  }, []);
  // Revoke any remaining object URLs on unmount.
  useEffect(() => () => { staged.forEach((s) => s.url && URL.revokeObjectURL(s.url)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canUpload = staged.length > 0 && (target === 'pro' ? !!uploadPro : !!uploadWhse) && !uploading;

  const submitUpload = async () => {
    setUploadBanner(null);
    setUploading(true);
    try {
      const files = staged.map((s) => s.file);
      if (target === 'pro' && uploadPro) {
        const { uploaded } = await warehousePhotosService.uploadToPro(uploadPro.pronumber, files);
        setUploadBanner({ kind: 'success', text: `Uploaded ${uploaded} photo(s) to pro ${uploadPro.pronumber}. The warehouse CSR has been notified.` });
      } else if (target === 'warehouse' && uploadWhse) {
        const { uploaded } = await warehousePhotosService.uploadToWarehouse(uploadWhse, files);
        setUploadBanner({ kind: 'success', text: `Uploaded ${uploaded} photo(s) to warehouse ${uploadWhse}.` });
        // If the assign section is viewing this warehouse, refresh it.
        if (assignWhse === uploadWhse) loadWarehousePhotos(uploadWhse);
      }
      clearStaged();
    } catch (err: any) {
      setUploadBanner({ kind: 'error', text: err?.response?.data?.detail || 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  // --- Section 2: assign ---------------------------------------------------
  // Deep-link support: the LC dashboard's Warehouse Photos panel opens this page with
  // ?assignWarehouse=<code> to land on a specific warehouse's photos to assign.
  const [searchParams] = useSearchParams();
  const assignSectionRef = useRef<HTMLDivElement>(null);
  const [assignWhse, setAssignWhse] = useState(() => searchParams.get('assignWarehouse') || '');
  // When deep-linked to a warehouse, scroll the assign section into view once on mount.
  useEffect(() => {
    if (searchParams.get('assignWarehouse')) {
      assignSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [photos, setPhotos] = useState<WarehousePhoto[]>([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [assignPro, setAssignPro] = useState<ProSummary | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [assignBanner, setAssignBanner] = useState<Banner>(null);

  const loadWarehousePhotos = useCallback(async (whse: string) => {
    if (!whse) { setPhotos([]); return; }
    setPhotosLoading(true);
    try {
      setPhotos(await warehousePhotosService.listWarehouse(whse));
      setSelected(new Set());
    } catch {
      setPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  }, []);

  useEffect(() => { loadWarehousePhotos(assignWhse); }, [assignWhse, loadWarehousePhotos]);

  const toggleSelect = (recno: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(recno) ? next.delete(recno) : next.add(recno);
      return next;
    });
  };

  const removePhoto = async (recno: number) => {
    try {
      await warehousePhotosService.deletePhoto(recno);
      setPhotos((prev) => prev.filter((p) => p.recno !== recno));
      setSelected((prev) => { const n = new Set(prev); n.delete(recno); return n; });
    } catch {
      setAssignBanner({ kind: 'error', text: 'Failed to remove photo.' });
    }
  };

  const canAssign = selected.size > 0 && !!assignPro && !assigning;

  const submitAssign = async () => {
    if (!assignPro) return;
    setAssignBanner(null);
    setAssigning(true);
    try {
      const recnos = Array.from(selected);
      const { moved } = await warehousePhotosService.assignToPro(assignPro.pronumber, recnos);
      setAssignBanner({ kind: 'success', text: `Moved ${moved} photo(s) to pro ${assignPro.pronumber}.` });
      await loadWarehousePhotos(assignWhse); // moved photos drop off the list
    } catch (err: any) {
      setAssignBanner({ kind: 'error', text: err?.response?.data?.detail || 'Assignment failed.' });
    } finally {
      setAssigning(false);
    }
  };

  // Import legacy-added folder photos on demand, then refresh the current view.
  const [rescanning, setRescanning] = useState(false);
  const runRescan = async () => {
    setAssignBanner(null);
    setRescanning(true);
    try {
      const { created, skipped } = await warehousePhotosService.rescanLegacy();
      setAssignBanner({
        kind: 'success',
        text: created > 0
          ? `Imported ${created} legacy photo(s) (${skipped} already tracked).`
          : `No new legacy photos found (${skipped} already tracked).`,
      });
      if (assignWhse) await loadWarehousePhotos(assignWhse);
    } catch (err: any) {
      setAssignBanner({ kind: 'error', text: err?.response?.data?.detail || 'Rescan failed.' });
    } finally {
      setRescanning(false);
    }
  };

  const BannerView: React.FC<{ banner: Banner }> = ({ banner }) =>
    banner ? (
      <div className={`mt-3 flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
        banner.kind === 'success'
          ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
          : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      }`}>
        {banner.kind === 'success' ? <CheckCircleIcon className="h-4 w-4" /> : <ExclamationTriangleIcon className="h-4 w-4" />}
        {banner.text}
      </div>
    ) : null;

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center gap-2">
        <PhotoIcon className="h-6 w-6 text-slate-600 dark:text-slate-300" />
        <h1 className="text-xl font-bold text-body">Warehouse Photos</h1>
      </div>

      {/* Section 1 — Upload */}
      <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-panel p-4 shadow-sm">
        <h2 className="mb-3 text-base font-semibold text-body">Upload Photos</h2>

        <div className="mb-4 inline-flex rounded-md border border-gray-300 dark:border-gray-600 p-0.5">
          {(['pro', 'warehouse'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                if (t === target) return;
                setTarget(t);
                // Clear the other target's selection so stale details don't linger
                // (e.g. pro details remaining after switching pro → warehouse → pro).
                setUploadPro(null);
                setUploadWhse('');
              }}
              className={`rounded px-3 py-1.5 text-sm font-medium transition-colors ${
                target === t ? 'bg-slate-700 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              {t === 'pro' ? 'Assign to Pro' : 'Assign to Warehouse'}
            </button>
          ))}
        </div>

        <div className="mb-4">
          {target === 'pro' ? (
            <ProLookup pro={uploadPro} onResolved={setUploadPro} />
          ) : (
            <div>
              <label className="block text-sm font-medium text-label mb-1">Warehouse</label>
              <SingleSelect
                options={whseOptions}
                value={uploadWhse}
                onChange={setUploadWhse}
                placeholder="Select a warehouse"
                enableSearch
                searchPlaceholder="Search warehouses…"
                className="min-w-[260px]"
              />
            </div>
          )}
        </div>

        <StagedUploader staged={staged} onAdd={addStaged} onRemove={removeStaged} disabled={uploading} />

        <div className="mt-4 flex items-center justify-end gap-3">
          {staged.length > 0 && (
            <button type="button" onClick={clearStaged} disabled={uploading} className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={submitUpload}
            disabled={!canUpload}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : 'Save'}
          </button>
        </div>
        <BannerView banner={uploadBanner} />
      </section>

      {/* Section 2 — Assign warehouse photos to a pro */}
      <section ref={assignSectionRef} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-panel p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-body">Assign Warehouse Photos to a Pro</h2>
          <button
            type="button"
            onClick={runRescan}
            disabled={rescanning}
            title="Import warehouse photos added through the legacy system"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${rescanning ? 'animate-spin' : ''}`} />
            {rescanning ? 'Rescanning…' : 'Rescan legacy photos'}
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-label mb-1">Warehouse</label>
          <SingleSelect
            options={whseOptions}
            value={assignWhse}
            onChange={setAssignWhse}
            placeholder="Select a warehouse to view its photos"
            enableSearch
            searchPlaceholder="Search warehouses…"
            className="min-w-[260px]"
          />
        </div>

        <div className="mb-4">
          {!assignWhse ? (
            <p className="text-sm text-gray-500">Select a warehouse to see its un-assigned photos.</p>
          ) : photosLoading ? (
            <p className="text-sm text-gray-500">Loading photos…</p>
          ) : photos.length === 0 ? (
            <p className="text-sm text-gray-500">No un-assigned photos for this warehouse.</p>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">{photos.length} photo(s) · {selected.size} selected</span>
                <button
                  type="button"
                  onClick={() => setSelected(selected.size === photos.length ? new Set() : new Set(photos.map((p) => p.recno)))}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selected.size === photos.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {photos.map((p) => (
                  <WarehousePhotoCard
                    key={p.recno}
                    photo={p}
                    selected={selected.has(p.recno)}
                    onToggle={() => toggleSelect(p.recno)}
                    onRemove={() => removePhoto(p.recno)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mb-4">
          <ProLookup pro={assignPro} onResolved={setAssignPro} />
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={submitAssign}
            disabled={!canAssign}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {assigning ? 'Assigning…' : `Assign ${selected.size || ''} to Pro`}
          </button>
        </div>
        <BannerView banner={assignBanner} />
      </section>
    </div>
  );
};

export default WarehousePhotos;
